/**
 * Modèle d'un personnage VtM V5 : structure stockée dans Firestore (collection `characters`),
 * valeurs dérivées, dégâts et validation des étapes de création.
 */
import {SYSTEMS} from '../utils/gameSystem';
import {ATTRIBUTE_IDS, SKILL_IDS, SKILLS} from './data/traits';
import {CLANS_BY_ID} from './data/clans';
import {POWERS_BY_ID} from './data/disciplines';
import {
  ATTRIBUTE_DISTRIBUTION,
  SKILL_DISTRIBUTIONS_BY_ID,
  CREATION_DISCIPLINE_DOTS,
  FREE_SPECIALTIES,
  ADVANTAGE_POINTS,
  FLAW_POINTS,
  STARTING_HUMANITY,
} from './data/creation';

export const CHARACTER_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
};

const fill = (ids, value) => ids.reduce((acc, id) => ({...acc, [id]: value}), {});

export const createEmptySheet = () => ({
  concept: '',
  ambition: '',
  desire: '',
  sire: '',
  clan: null,
  ageCategory: 'neonate',
  generation: 13,
  predatorType: null,
  bloodPotency: 1,
  attributes: fill(ATTRIBUTE_IDS, 1),
  skills: fill(SKILL_IDS, 0),
  skillDistribution: null,
  // [{skill: 'persuasion', name: 'Négociation', source: 'free' | 'required' | 'predator'}]
  specialties: [],
  // {celerity: {level: 2, powers: ['catsGrace', 'rapidReflexes']}}
  disciplines: {},
  // Notes libres du joueur sur ses pouvoirs (pas de texte de règles) : {catsGrace: 'Esquive sans action'}
  powerNotes: {},
  predatorDiscipline: null,
  health: {superficial: 0, aggravated: 0},
  willpower: {superficial: 0, aggravated: 0},
  humanity: STARTING_HUMANITY,
  stains: 0,
  hunger: 1,
  // [{kind: 'advantage' | 'flaw', name: '', description: '', level: 1-5, source: 'creation' | 'predator' | 'xp'}]
  advantages: [],
  // [{conviction: '', touchstone: ''}]
  convictions: [],
  xp: {total: 0, spent: 0},
  notes: '',
});

/**
 * Document Firestore d'un nouveau personnage VtM. Les champs communs (uid, idUser, idCampaign, name, picture)
 * restent au premier niveau comme pour Aria ; tout ce qui est propre à VtM est sous `vtm`.
 */
export const createVtmCharacter = ({uid, name = '', idUser, idCampaign, picture = null}) => ({
  uid,
  name,
  idUser,
  idCampaign,
  picture,
  system: SYSTEMS.VTM5,
  status: CHARACTER_STATUS.DRAFT,
  vtm: createEmptySheet(),
});

// ——— Valeurs dérivées ———

export const ADVANTAGE_KIND = {
  ADVANTAGE: 'advantage',
  FLAW: 'flaw',
};

export const MAX_ADVANTAGE_LEVEL = 5;

export const createAdvantage = ({kind = ADVANTAGE_KIND.ADVANTAGE, name = '', description = '', level = 1, source = 'creation'} = {}) => ({
  kind,
  name,
  description,
  level,
  source,
});

const disciplineHasPower = (sheet, disciplineId, powerId) => (sheet.disciplines[disciplineId]?.powers || []).includes(powerId);

/** Santé max = Vigueur + 3 (+ niveau de Force d'âme avec Résilience). */
export const getHealthMax = (sheet) => {
  const resilience = disciplineHasPower(sheet, 'fortitude', 'resilience') ? sheet.disciplines.fortitude.level : 0;
  return sheet.attributes.stamina + 3 + resilience;
};

/** Volonté max = Sang-froid + Résolution. */
export const getWillpowerMax = (sheet) => sheet.attributes.composure + sheet.attributes.resolve;

/** Cases sans aucun dégât (la Volonté restante sert notamment aux tests de frénésie). */
export const getRemaining = (track, max) => Math.max(0, max - track.superficial - track.aggravated);

/** Diminué (Impaired) : toutes les cases de la jauge ont un dégât. */
export const isImpaired = (track, max) => getRemaining(track, max) === 0;

/**
 * Applique des dégâts à une jauge (Santé ou Volonté).
 * Quand il n'y a plus de case libre, chaque dégât superficiel supplémentaire transforme un superficiel en aggravé.
 * @param {{superficial: Number, aggravated: Number}} track
 * @param {Number} max
 * @param {{superficial?: Number, aggravated?: Number}} damage
 */
export const applyDamage = (track, max, {superficial = 0, aggravated = 0}) => {
  let next = {...track};
  for (let i = 0; i < aggravated; i += 1) {
    if (next.superficial + next.aggravated < max) {
      next.aggravated += 1;
    } else if (next.superficial > 0) {
      next = {superficial: next.superficial - 1, aggravated: next.aggravated + 1};
    }
  }
  for (let i = 0; i < superficial; i += 1) {
    if (next.superficial + next.aggravated < max) {
      next.superficial += 1;
    } else if (next.superficial > 0) {
      next = {superficial: next.superficial - 1, aggravated: next.aggravated + 1};
    }
  }
  return next;
};

/** Soin des dégâts superficiels (les aggravés restent). */
export const mendTrack = (track, amount) => ({...track, superficial: Math.max(0, track.superficial - amount)});

/** Le personnage est hors de combat (Santé) ou brisé (Volonté) : toute la jauge en aggravé. */
export const isTrackDestroyed = (track, max) => track.aggravated >= max;

// ——— Validation de la création ———

const countByValue = (values) => values.reduce((acc, value) => {
  if (value > 0) acc[value] = (acc[value] || 0) + 1;
  return acc;
}, {});

const compareDistribution = (actual, expected) => {
  const keys = new Set([...Object.keys(actual), ...Object.keys(expected)]);
  const errors = [];
  keys.forEach(key => {
    const have = actual[key] || 0;
    const want = expected[key] || 0;
    if (have !== want) errors.push({value: Number(key), have, want});
  });
  return errors;
};

/** Attributs : un à 4, trois à 3, quatre à 2, un à 1. */
export const validateAttributes = (attributes) => {
  const errors = compareDistribution(countByValue(ATTRIBUTE_IDS.map(id => attributes[id] || 0)), ATTRIBUTE_DISTRIBUTION);
  return {valid: errors.length === 0, errors};
};

/** Compétences selon le profil choisi (Touche-à-tout, Équilibré, Spécialiste). */
export const validateSkills = (skills, distributionId) => {
  const distribution = SKILL_DISTRIBUTIONS_BY_ID[distributionId];
  if (!distribution) return {valid: false, errors: [{reason: 'noDistribution'}]};
  const errors = compareDistribution(countByValue(SKILL_IDS.map(id => skills[id] || 0)), distribution.distribution);
  return {valid: errors.length === 0, errors};
};

/**
 * Spécialités : une obligatoire par compétence qui l'exige (si au moins 1 point), une libre, et celle du prédateur.
 * Une spécialité ne peut porter que sur une compétence ayant au moins 1 point.
 */
export const validateSpecialties = (skills, specialties, {withPredator = true} = {}) => {
  const errors = [];
  specialties.forEach(item => {
    if (!item.name || !item.name.trim()) errors.push({reason: 'emptyName', skill: item.skill});
    if (!(skills[item.skill] > 0)) errors.push({reason: 'skillWithoutDots', skill: item.skill});
  });
  SKILLS.filter(skill => skill.requiresSpecialty && skills[skill.id] > 0).forEach(skill => {
    if (!specialties.some(item => item.skill === skill.id && item.source === 'required')) {
      errors.push({reason: 'missingRequired', skill: skill.id});
    }
  });
  const free = specialties.filter(item => item.source === 'free').length;
  if (free !== FREE_SPECIALTIES) errors.push({reason: 'freeCount', have: free, want: FREE_SPECIALTIES});
  if (withPredator && !specialties.some(item => item.source === 'predator')) {
    errors.push({reason: 'missingPredator'});
  }
  return {valid: errors.length === 0, errors};
};

/**
 * Disciplines : 2 points dans une discipline de clan, 1 dans une autre, plus le point du prédateur
 * (`predatorDiscipline`). Le Caïtiff choisit librement ; le Sang-Clair n'a pas de discipline de clan à répartir.
 */
export const validateDisciplines = (disciplines, clanId, predatorDiscipline) => {
  const clan = CLANS_BY_ID[clanId];
  if (!clan) return {valid: false, errors: [{reason: 'noClan'}]};
  const levels = Object.entries(disciplines).reduce((acc, [id, value]) => ({...acc, [id]: value.level || 0}), {});
  if (predatorDiscipline) {
    if (!(levels[predatorDiscipline] > 0)) {
      return {valid: false, errors: [{reason: 'missingPredatorDot', discipline: predatorDiscipline}]};
    }
    levels[predatorDiscipline] -= 1;
  }
  if (clan.thinBlood) {
    const extra = Object.values(levels).some(level => level > 0);
    return {valid: !extra, errors: extra ? [{reason: 'thinBloodNoClanDots'}] : []};
  }
  const errors = [];
  const chosen = Object.entries(levels).filter(([, level]) => level > 0);
  const dots = chosen.map(([, level]) => level).sort((a, b) => b - a);
  if (dots.join(',') !== CREATION_DISCIPLINE_DOTS.join(',')) {
    errors.push({reason: 'dots', have: dots, want: CREATION_DISCIPLINE_DOTS});
  }
  if (!clan.anyDisciplines) {
    chosen.filter(([id]) => !clan.disciplines.includes(id)).forEach(([id]) => errors.push({reason: 'notClanDiscipline', discipline: id}));
  }
  return {valid: errors.length === 0, errors};
};

/**
 * Paliers : le pouvoir du n-ième point est de niveau ≤ n. Une fois les niveaux triés, le n-ième ne dépasse pas n
 * (Auspex 3 : 1+2+3 ou 1+1+3, mais pas 2+2+2 ni 1+3+3).
 * Pour une sélection partielle, on vérifie qu'elle reste complétable : les pouvoirs choisis occupent
 * les paliers les plus hauts, le j-ième plus élevé ne dépasse pas (niveau − j + 1).
 * @param {Number[]} powerLevels niveaux des pouvoirs choisis
 * @param {Number} level niveau de la discipline
 */
export const respectsPowerTiers = (powerLevels, level) => [...powerLevels]
  .sort((a, b) => b - a)
  .every((powerLevel, index) => powerLevel <= level - index);

/** Pouvoirs : autant que de points, par paliers (cf. respectsPowerTiers), amalgames respectés. */
export const validatePowers = (disciplines) => {
  const errors = [];
  Object.entries(disciplines).forEach(([disciplineId, {level = 0, powers = []}]) => {
    if (powers.length !== level) errors.push({reason: 'count', discipline: disciplineId, have: powers.length, want: level});
    powers.forEach(powerId => {
      const item = POWERS_BY_ID[powerId];
      if (!item || item.discipline !== disciplineId) {
        errors.push({reason: 'unknownPower', discipline: disciplineId, power: powerId});
        return;
      }
      if (item.level > level) errors.push({reason: 'levelTooHigh', discipline: disciplineId, power: powerId});
      if (item.amalgam && (disciplines[item.amalgam.discipline]?.level || 0) < item.amalgam.level) {
        errors.push({reason: 'amalgam', discipline: disciplineId, power: powerId});
      }
    });
    const levels = powers.map(powerId => POWERS_BY_ID[powerId]?.level).filter(Boolean);
    if (levels.every(powerLevel => powerLevel <= level) && !respectsPowerTiers(levels, level)) {
      errors.push({reason: 'tiers', discipline: disciplineId});
    }
  });
  return {valid: errors.length === 0, errors};
};

/**
 * Avantages et handicaps : un nom, une valeur de 1 à 5 (la description est libre).
 * Budget de création : 7 points d'avantages et 2 points de handicaps ; ceux donnés par le prédateur sont en plus.
 * @param {Object[]} advantages
 * @param {{checkBudget?: Boolean}} [options] false hors création (avantages achetés en XP, ajouts du MJ)
 */
export const validateAdvantages = (advantages, {checkBudget = true} = {}) => {
  const errors = [];
  advantages.forEach((item, index) => {
    if (!Object.values(ADVANTAGE_KIND).includes(item.kind)) errors.push({reason: 'kind', index});
    if (!item.name || !item.name.trim()) errors.push({reason: 'emptyName', index});
    if (!Number.isInteger(item.level) || item.level < 1 || item.level > MAX_ADVANTAGE_LEVEL) errors.push({reason: 'level', index});
  });
  if (checkBudget) {
    const spent = (kind) => advantages
      .filter(item => item.kind === kind && item.source === 'creation')
      .reduce((sum, item) => sum + (item.level || 0), 0);
    const advantagePoints = spent(ADVANTAGE_KIND.ADVANTAGE);
    const flawPoints = spent(ADVANTAGE_KIND.FLAW);
    if (advantagePoints !== ADVANTAGE_POINTS) errors.push({reason: 'advantageBudget', have: advantagePoints, want: ADVANTAGE_POINTS});
    if (flawPoints !== FLAW_POINTS) errors.push({reason: 'flawBudget', have: flawPoints, want: FLAW_POINTS});
  }
  return {valid: errors.length === 0, errors};
};

// ——— Édition des jauges depuis la fiche ———

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Clic sur la case n° `index` d'une jauge de points (Soif, Humanité) :
 * fixe la valeur à cette case, ou retire un point si on reclique sur la dernière case pleine.
 */
export const valueFromClick = (current, index, max) => clamp(index + 1 === current ? index : index + 1, 0, max);

/**
 * Clic sur une case de Santé ou de Volonté : vide → superficiel → aggravé → vide.
 * Les cases sont ensuite réordonnées (aggravés, superficiels, vides).
 */
export const cycleDamageBox = (track, max, index) => {
  const states = Array.from({length: max}, (_, i) => {
    if (i < track.aggravated) return 'aggravated';
    if (i < track.aggravated + track.superficial) return 'superficial';
    return 'empty';
  });
  const next = {empty: 'superficial', superficial: 'aggravated', aggravated: 'empty'};
  states[index] = next[states[index]];
  return {
    superficial: states.filter(state => state === 'superficial').length,
    aggravated: states.filter(state => state === 'aggravated').length,
  };
};

/** Nouvelle Humanité : les souillures sont réduites si elles n'ont plus de place. */
export const setHumanity = (humanity, stains) => {
  const value = clamp(humanity, 0, 10);
  return {humanity: value, stains: clamp(stains, 0, 10 - value)};
};

/** Souillures : entre 0 et les cases d'Humanité non remplies. */
export const adjustStains = (humanity, stains, delta) => clamp(stains + delta, 0, 10 - humanity);

/** XP : le total ne descend pas sous l'XP dépensée, la dépense ne dépasse pas le total. */
export const adjustXp = (xp, {total = 0, spent = 0}) => {
  const nextTotal = Math.max(xp.spent, xp.total + total, 0);
  return {total: nextTotal, spent: clamp(xp.spent + spent, 0, nextTotal)};
};
